import { css } from 'aphrodite';
import { useEffect, useState } from 'react';
import { styleSheet as styles } from './styles/KeyboardShortcutsHelpStyles';

interface KeyboardShortcut {
	keys: string[];
	description: string;
	category: string;
}

const shortcuts: KeyboardShortcut[] = [
	{ keys: ['?'], description: 'Show keyboard shortcuts', category: 'General' },
	{ keys: ['Esc'], description: 'Close dialogs and menus / Cancel drag', category: 'General' },
	{ keys: ['Ctrl', 'F'], description: 'Search steps by name or type', category: 'General' },
	{ keys: ['/'], description: 'Search steps by name or type', category: 'General' },
	{ keys: ['Ctrl', 'A'], description: 'Select all nodes', category: 'Selection' },
	{ keys: ['Ctrl', 'B'], description: 'Select branch from selected node', category: 'Selection' },
	{ keys: ['Shift', 'Click'], description: 'Add to selection', category: 'Selection' },
	{ keys: ['Drag'], description: 'Move selected nodes as a group', category: 'Selection' },
	{ keys: ['Ctrl', 'C'], description: 'Copy selected nodes', category: 'Editing' },
	{ keys: ['Ctrl', 'V'], description: 'Paste copied nodes', category: 'Editing' },
	{ keys: ['Ctrl', 'D'], description: 'Duplicate selected nodes', category: 'Editing' },
	{ keys: ['Ctrl', 'Z'], description: 'Undo last action', category: 'Editing' },
	{ keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo last action', category: 'Editing' },
	{ keys: ['Ctrl', 'Y'], description: 'Redo last action', category: 'Editing' },
	{ keys: ['Delete'], description: 'Delete selected nodes', category: 'Editing' },
	{ keys: ['Backspace'], description: 'Delete selected nodes', category: 'Editing' },
	{ keys: ['F'], description: 'Fit view to canvas', category: 'Navigation' },
	{ keys: ['Z'], description: 'Zoom to selected nodes', category: 'Navigation' },
	{ keys: ['Ctrl', 'E'], description: 'Expand/collapse all nodes', category: 'Navigation' },
	{ keys: ['Ctrl', '0'], description: 'Fit view to canvas', category: 'Navigation' },
	{ keys: ['Ctrl', '+'], description: 'Zoom in', category: 'Navigation' },
	{ keys: ['Ctrl', '-'], description: 'Zoom out', category: 'Navigation' },
	{ keys: ['Tab'], description: 'Navigate between nodes', category: 'Navigation' },
	{ keys: ['Enter'], description: 'Expand/collapse node details', category: 'Editing' },
];

interface KeyboardShortcutsHelpProps {
	isOpen: boolean;
	onClose: () => void;
}

export function KeyboardShortcutsHelp({ isOpen, onClose }: KeyboardShortcutsHelpProps) {
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				onClose();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, onClose]);

	useEffect(() => {
		if (isOpen) {
			const modal = document.getElementById('keyboard-shortcuts-modal');
			modal?.focus();
		}
	}, [isOpen]);

	if (!isOpen) {
		return null;
	}

	const categories = Array.from(new Set(shortcuts.map(s => s.category)));

	return (
		<div
			className={css(styles.overlay)}
			onClick={onClose}
			role='dialog'
			aria-labelledby='keyboard-shortcuts-title'
			aria-modal='true'
		>
			<div id='keyboard-shortcuts-modal' className={css(styles.modal)} onClick={e => e.stopPropagation()} tabIndex={-1}>
				<div className={css(styles.header)}>
					<h2 id='keyboard-shortcuts-title' className={css(styles.title)}>
						Keyboard Shortcuts
					</h2>
					<button
						onClick={onClose}
						className={css(styles.closeButton)}
						type='button'
						aria-label='Close keyboard shortcuts dialog'
					>
						<svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
							<path d='M5 5l10 10M15 5L5 15' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
						</svg>
					</button>
				</div>
				<div className={css(styles.content)}>
					{categories.map(category => (
						<div key={category} className={css(styles.category)}>
							<h3 className={css(styles.categoryTitle)}>{category}</h3>
							<div className={css(styles.shortcutsList)}>
								{shortcuts
									.filter(s => s.category === category)
									.map((shortcut, index) => (
										<div key={index} className={css(styles.shortcutItem)}>
											<div className={css(styles.keysContainer)}>
												{shortcut.keys.map((key, keyIndex) => (
													<span key={keyIndex}>
														<kbd className={css(styles.key)}>{key}</kbd>
														{keyIndex < shortcut.keys.length - 1 && <span className={css(styles.keySeparator)}>+</span>}
													</span>
												))}
											</div>
											<span className={css(styles.description)}>{shortcut.description}</span>
										</div>
									))}
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

export function useKeyboardShortcuts() {
	const [isHelpOpen, setIsHelpOpen] = useState(false);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
				e.preventDefault();
				setIsHelpOpen(true);
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, []);

	return {
		isHelpOpen,
		openHelp: () => setIsHelpOpen(true),
		closeHelp: () => setIsHelpOpen(false),
	};
}
